"use client"

import { Popover, PopoverContent, PopoverTrigger } from "@repo/shad/ui/popover"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@repo/shad/ui/sidebar"
import { type Icon, IconRocket } from "@tabler/icons-react"
import * as React from "react"

import { ExpandableCard } from "../expandable-card"
import type { NavAction } from "../types"

export function NavSecondary({
  items,
  actions,
  ...props
}: {
  items: {
    title: string
    url: string
    icon: Icon
    badge?: string
  }[]
  actions?: Record<string, NavAction>
} & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  return (
    <SidebarGroup {...props}>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const action = actions?.[item.title]

            const badge = item.badge ? (
              <span className="text-xs text-card-foreground/40 ml-auto">{item.badge}</span>
            ) : null

            if (action?.type === "card") {
              return (
                <SidebarMenuItem key={item.title}>
                  <ExpandableCard.Trigger cardId={action.cardId} onClick={action.onClick}>
                    <SidebarMenuButton>
                      <item.icon />
                      <span>{item.title}</span>
                      {badge}
                    </SidebarMenuButton>
                  </ExpandableCard.Trigger>
                </SidebarMenuItem>
              )
            }

            if (action?.type === "comingSoon") {
              return (
                <SidebarMenuItem key={item.title}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <SidebarMenuButton>
                        <item.icon />
                        <span>{item.title}</span>
                        {badge}
                      </SidebarMenuButton>
                    </PopoverTrigger>
                    <PopoverContent side="top" align="start" className="w-48">
                      <div className="flex flex-col items-center gap-2 text-center">
                        <IconRocket className="size-6 text-muted-foreground" />
                        <p className="text-sm font-medium">{item.title}</p>
                        <p className="text-xs text-muted-foreground">Coming soon!</p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </SidebarMenuItem>
              )
            }

            const isExternal = item.url.startsWith("http")

            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild>
                  <a
                    href={item.url}
                    {...(isExternal && { target: "_blank", rel: "noopener noreferrer" })}
                  >
                    <item.icon />
                    <span>{item.title}</span>
                    {badge}
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
